import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';

import {
  getReceivedModelById,
  getReceivedModelOutput,
  type GetReceivedModelOutputInput,
  getReceivedModels,
  type GetReceivedModelsInput,
  importReceivedModel,
  type ImportReceivedModelInput,
} from '../../api/receivedModel';
import { type GetSharedModelByIdInput } from '../../api/sharedModel';
import { type TypeSharedModel } from '../../common/dataTypes';
import { type DefaultMutationError, type DefaultQueryError } from '../index';

export const useGetReceivedModelOutput = (
  input: GetReceivedModelOutputInput,
  opts?: Partial<
    UseQueryOptions<
      Awaited<Record<string, Record<string, number[]>>>,
      DefaultQueryError
    >
  >
) =>
  useQuery({
    queryKey: ['getReceivedModelOutput', input],
    queryFn: async () => {
      const {
        data: model,
      }: AxiosResponse<Record<string, Record<string, number[]>>> =
        await getReceivedModelOutput(input);

      return model;
    },
    ...opts,
  });

export const useImportReceivedModel = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['importReceivedModel'],
    mutationFn: async (input: ImportReceivedModelInput) => {
      const { data }: AxiosResponse = await importReceivedModel(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: [
          'getModels',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useGetReceivedModels = (
  input: GetReceivedModelsInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeSharedModel[]>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getReceivedModels', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeSharedModel[]> =
        await getReceivedModels(input);

      return data;
    },
    ...opts,
  });

export const useGetReceivedModelById = (
  input: GetSharedModelByIdInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeSharedModel>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getReceivedModelById', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeSharedModel> =
        await getReceivedModelById(input);

      return data;
    },
    ...opts,
  });
